/**
 * What the centre of a pipeline page shows before a study is open.
 *
 * It used to show "No case open — open or create a case from the Home tab",
 * which is a third of the window spent telling you to go somewhere else. The
 * whole point of the DWI page is that it is where the DWI work happens; being
 * sent away to begin it is the same navigation problem as before, wearing a
 * different hat.
 *
 * So the space does the job instead. Choose a folder here, see what was found
 * in it, correct anything the detector got wrong, and open it — without
 * leaving the page you came to.
 *
 * Three ways in, because a folder can be three things and the reader should
 * not have to know which they have: one subject, a folder of subjects, or a
 * case somebody already made. `openSubjectOrCase` decides, so a wrong guess
 * costs nothing.
 */

import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FolderOpen, Loader2, Table2, XCircle } from "lucide-react";

import { Button, Select } from "@/components/ui/primitives";
import { pickDirectory, prepareSubjectInput, splitError } from "@/ipc/api";
import {
  STAGEABLE_ROLES,
  type DetectedFile,
  type PipelineKind,
  type RoleChoice,
  type Severity,
  type StageableRole,
} from "@/ipc/contract";
import { cn } from "@/lib/utils";
import { goToTab, openSubjectOrCase } from "@/pipeline/actions";
import { usePipelineSession } from "@/pipeline/session";
import { useAppStore } from "@/store/appStore";

const SEVERITY_ICON: Record<Severity, typeof CheckCircle2> = {
  ok: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
};

const SEVERITY_CLASS: Record<Severity, string> = {
  ok: "text-emerald-600",
  warning: "text-amber-600",
  error: "text-red-600",
};

const KIND_TITLE: Record<PipelineKind, string> = {
  dwi: "Diffusion study",
  pwi: "Perfusion study",
  combined: "Diffusion and perfusion study",
};

/**
 * Where to be once the folder has opened. The whole point of starting here is
 * not being sent somewhere else, so the page that asked keeps the reader —
 * `openSubjectOrCase` otherwise lands everyone in the viewer.
 */
const KIND_TAB = { dwi: "dwi", pwi: "perfusion", combined: "pipeline" } as const;

const KIND_WANTS: Record<PipelineKind, string> = {
  dwi: "a DWI volume, and an ADC or a b0 to compute one from",
  pwi: "a 4D perfusion series with its JSON sidecar, plus the diffusion images",
  combined: "both the diffusion images and the 4D perfusion series",
};

function formatBytes(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  return `${(n / 1e3).toFixed(0)} kB`;
}

/**
 * One detected file, with what the app took it to be — and a way to say it is
 * wrong.
 *
 * The detector reads names, so it is right most of the time and confidently
 * wrong the rest: two files that both look like an ADC, a corrected volume
 * beside the original, a naming scheme the patterns do not cover. Leaving the
 * reader to rename their own data to satisfy a guess is the wrong way round.
 */
function FileLine({
  file,
  assigned,
  onAssign,
}: {
  file: DetectedFile;
  assigned: StageableRole | "";
  onAssign: (role: StageableRole | "") => void;
}) {
  const Icon = SEVERITY_ICON[file.status];
  const missing = file.sizeBytes === 0 && file.dimensions == null;
  return (
    <div className="flex items-start gap-2.5 border-b border-ads-border px-4 py-2 last:border-0">
      <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", SEVERITY_CLASS[file.status])} />
      {missing ? (
        <span className="mt-px w-[5.5rem] shrink-0 rounded bg-ads-bg px-1.5 py-0.5 text-center text-[10px] font-semibold text-neutral-400">
          {file.modality}
        </span>
      ) : (
        <Select
          className="mt-px h-6 w-[5.5rem] shrink-0 text-[10.5px]"
          value={assigned}
          aria-label={`Role for ${file.fileName}`}
          onChange={(e) => onAssign(e.target.value as StageableRole | "")}
        >
          <option value="">Ignore</option>
          {STAGEABLE_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-mono text-[11.5px] text-neutral-800">
          {file.fileName}
        </span>
        <span className="block text-[10.5px] text-neutral-500">
          {file.dimensions?.length ? `${file.dimensions.join(" × ")} · ` : ""}
          {file.voxelSizeMm?.length
            ? `${file.voxelSizeMm.map((v) => v.toFixed(2)).join(" × ")} mm · `
            : ""}
          {file.sizeBytes > 0 ? `${formatBytes(file.sizeBytes)} · ` : ""}
          {file.detail}
        </span>
      </span>
    </div>
  );
}

export function StudyStart({ kind }: { kind: PipelineKind }) {
  const scan = usePipelineSession((s) => s.scan);
  const scanning = usePipelineSession((s) => s.scanning);
  const scanError = usePipelineSession((s) => s.scanError);
  const draft = usePipelineSession((s) => s.draft);
  const defaultRoot = useAppStore((s) => s.preferences?.defaultInputRoot ?? null);
  const chooseSubjectDir = usePipelineSession((s) => s.chooseSubjectDir);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** Path → role, for the files the reader has re-assigned. */
  const [overrides, setOverrides] = useState<Record<string, StageableRole | "">>({});

  /**
   * What each file is currently taken to be: the detector's answer unless the
   * reader has overruled it. A role the detector did not recognise shows as
   * "Ignore", which is what it means.
   */
  const assignedRole = (file: DetectedFile): StageableRole | "" => {
    const override = overrides[file.path];
    if (override !== undefined) return override;
    return (STAGEABLE_ROLES as readonly string[]).includes(file.modality)
      ? (file.modality as StageableRole)
      : "";
  };

  const changed = Object.keys(overrides).length > 0;

  /** Every assignment, as the staging command wants it. */
  const choices = useMemo((): RoleChoice[] => {
    if (!scan) return [];
    const out: RoleChoice[] = [];
    for (const file of scan.files) {
      if (file.sizeBytes === 0 && file.dimensions == null) continue; // a file that is not there
      const role = overrides[file.path] ?? (
        (STAGEABLE_ROLES as readonly string[]).includes(file.modality) ? file.modality : ""
      );
      if (role) out.push({ role: role as StageableRole, path: file.path });
    }
    return out;
  }, [scan, overrides]);

  /** Roles claimed twice, which the backend refuses and the reader should see first. */
  const duplicated = useMemo(() => {
    const seen = new Map<string, number>();
    for (const c of choices) seen.set(c.role, (seen.get(c.role) ?? 0) + 1);
    return [...seen.entries()].filter(([, n]) => n > 1).map(([r]) => r);
  }, [choices]);

  const applyAssignment = async () => {
    if (!scan) return;
    setError(null);
    setNote(null);
    setBusy(true);
    try {
      const prepared = await prepareSubjectInput(scan.subjectId, choices);
      await chooseSubjectDir(prepared.dir);
      setOverrides({});
      setNote(
        prepared.copiedAny
          ? `Using your assignment. Some files had to be copied — they are on a different drive from the app's own folder.`
          : `Using your assignment. The files were linked, not copied, so nothing was duplicated and your folder is untouched.`,
      );
    } catch (e) {
      const { message, detail } = splitError(e);
      setError(detail ? `${message}\n${detail}` : message);
    } finally {
      setBusy(false);
    }
  };

  const choose = async () => {
    setError(null);
    setBusy(true);
    try {
      const { path } = await pickDirectory(
        "Choose a subject folder, a folder of subjects, or a saved case",
        defaultRoot ?? undefined,
      );
      if (path) await openSubjectOrCase(path, { tab: KIND_TAB[kind] });
    } catch (e) {
      setError(splitError(e).message);
    } finally {
      setBusy(false);
    }
  };

  const readiness =
    scan == null
      ? null
      : kind === "pwi"
        ? scan.readiness.pwi
        : kind === "combined"
          ? scan.readiness.combined
          : scan.readiness.dwi;

  return (
    <div className="h-full overflow-auto bg-ads-bg">
      <div className="mx-auto flex max-w-2xl flex-col gap-5 px-8 py-10">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400">
            {KIND_TITLE[kind]}
          </div>
          <h2 className="mt-1.5 text-[19px] font-semibold tracking-tight text-neutral-900">
            Choose the folder to work on
          </h2>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-neutral-500">
            One subject, a folder of subjects, or a case you saved earlier — ADS Desk works out
            which it is. This pipeline wants {KIND_WANTS[kind]}.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="primary" onClick={() => void choose()} disabled={busy || scanning}>
            {busy || scanning ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FolderOpen className="h-3.5 w-3.5" />
            )}
            {busy || scanning ? "Reading…" : "Choose folder…"}
          </Button>
          <Button variant="outline" onClick={() => goToTab("dataset")}>
            <Table2 className="h-3.5 w-3.5" />
            Scan a whole folder of subjects…
          </Button>
        </div>

        {error || scanError ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[11.5px] leading-relaxed text-red-800">
            {error ?? scanError}
          </p>
        ) : null}

        {/* Everything below appears once a folder has been read, so the choice
            can be checked before anything is created. */}
        {scan ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-neutral-900">{scan.subjectId}</div>
                <div className="truncate font-mono text-[10.5px] text-neutral-400">
                  {draft.subjectDir || scan.subjectDir}
                </div>
              </div>
              <div className="shrink-0 text-[11px] tabular-nums text-neutral-500">
                {scan.validCount} ok · {scan.warningCount} warning · {scan.errorCount} error
              </div>
            </div>

            {readiness ? (
              <p
                className={cn(
                  "rounded-lg border px-3.5 py-2.5 text-[11.5px] leading-relaxed",
                  readiness.ready
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-amber-200 bg-amber-50 text-amber-900",
                )}
              >
                {readiness.ready
                  ? "This folder has everything the pipeline needs."
                  : `Not ready yet: missing ${readiness.missing.join(", ")}.`}
                {readiness.notes.length ? ` ${readiness.notes.join(" ")}` : ""}
              </p>
            ) : null}

            <div className="overflow-hidden rounded-xl border border-ads-border bg-ads-surface">
              <div className="border-b border-ads-border bg-ads-subtle px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                What was found, and what it was taken to be
              </div>
              {scan.files.length === 0 ? (
                <p className="px-4 py-5 text-center text-[11.5px] text-neutral-500">
                  No images in this folder.
                </p>
              ) : (
                scan.files.map((f) => (
                  <FileLine
                    key={f.path}
                    file={f}
                    assigned={assignedRole(f)}
                    onAssign={(role) => setOverrides((o) => ({ ...o, [f.path]: role }))}
                  />
                ))
              )}
            </div>

            {duplicated.length ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[11.5px] leading-relaxed text-amber-900">
                Two files are both set as the {duplicated.join(" and the ")}. Each role takes one
                file.
              </p>
            ) : null}

            {changed ? (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-ads-accent-border bg-ads-accent-soft px-3.5 py-2.5">
                <span className="min-w-0 flex-1 text-[11.5px] leading-relaxed text-neutral-700">
                  The pipeline finds its inputs by file name, so using your assignment means
                  preparing a folder where the files you picked carry the names it looks for. Your
                  own folder is not changed.
                </span>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={busy || duplicated.length > 0}
                  onClick={() => void applyAssignment()}
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Use these files
                </Button>
                <Button size="sm" variant="outline" onClick={() => setOverrides({})}>
                  Reset
                </Button>
              </div>
            ) : null}

            {note ? (
              <p className="text-[11.5px] leading-relaxed text-neutral-500">{note}</p>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={() => goToTab("pipeline")}>
                Set up the run
              </Button>
              <Button variant="outline" onClick={() => void choose()}>
                Another folder…
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default StudyStart;
